import '@/index.css';

import { createRoot } from 'react-dom/client';

import { configureApi } from '@/api';
import { probeCloudConfig } from '@/api/bootConfig';
import { mediaServerStrictness } from '@/api/mediaServerStrictness';
import { fetchSentryConfig } from '@/api/runtimeConfig';
import { sessionRefresher } from '@/api/session';
import { App } from '@/App';
import { trackKeyboardFocus } from '@/app/keyboardFocus';
import { panelSwitches } from '@/app/panelSwitches';
import { reloadOnStaleChunk } from '@/app/staleChunkReload';
import { startPage } from '@/app/startPage';
import { authSession } from '@/auth/authSession';
import { sentryReporter } from '@/lib/sentry';
import { BootErrorPage } from '@/pages/BootErrorPage';

/**
 * The first render does not wait for the SDK, which is a large download on a slow network. The
 * reporter keeps what fails before the SDK runs, so a crash on boot reaches Sentry too.
 */
async function startErrorReporting(): Promise<void> {
    try {
        const config = await fetchSentryConfig();
        if (config === null) {
            sentryReporter.stop();
            return;
        }
        await sentryReporter.start(config, authSession);
    } catch (error) {
        sentryReporter.stop();
        console.error('[sentry] reporting is off for this page', error);
    }
}

/**
 * The cloud's config is the only source of the transport, and only the cloud answers the probe,
 * so the probe decides whether the app starts at all.
 */
(async (): Promise<void> => {
    sentryReporter.collect();
    reloadOnStaleChunk();
    trackKeyboardFocus();
    startErrorReporting();
    const [config] = await Promise.all([
        probeCloudConfig(), mediaServerStrictness.load(), panelSwitches.load(),
    ]);
    const container = document.getElementById('root');
    if (!container) {
        throw new Error('index.html has no #root to mount into');
    }
    const root = createRoot(container);
    startPage(config, {
        startApp: (cloudConfig): void => {
            configureApi(cloudConfig);
            sessionRefresher.start();
            root.render(<App />);
        },
        onFailed: (): void => {
            root.render(<BootErrorPage onRetry={(): void => window.location.reload()} />);
        },
    });
})();
